// src/pages/CourseTime/styles/discussion.styles.js
import styled, { keyframes } from 'styled-components';
import { PHONE_LANDSCAPE } from './slides.styles';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Floating Chat Button
export const FloatingChatButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.themeColor || '#32baf1'};
  border: none;
  box-shadow: 0 4px 20px ${props => props.themeColor ? `${props.themeColor}4D` : 'rgba(50, 186, 241, 0.3)'};
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px ${props => props.themeColor ? `${props.themeColor}66` : 'rgba(50, 186, 241, 0.4)'};
    animation: ${pulse} 2s infinite;
  }
  
  &:active {
    transform: translateY(0);
  }
  
  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    bottom: 20px;
    right: 20px;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media ${PHONE_LANDSCAPE} {
    width: 44px;
    height: 44px;
    bottom: 12px;
    right: 12px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

// Unread Messages Badge
export const UnreadBadge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: #dc2626;
  color: white;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
  border: 2px solid white;
  animation: ${fadeIn} 0.3s ease-out;
  
  ${props => props.count > 99 && `
    font-size: 10px;
    min-width: 24px;
    height: 20px;
  `}
  
  @media (max-width: 768px) {
    top: -1px;
    right: -1px;
    min-width: 18px;
    height: 18px;
    font-size: 11px;
    border: 1px solid white;
    
    ${props => props.count > 99 && `
      font-size: 9px;
      min-width: 22px;
      height: 18px;
    `}
  }
`;

// Modal Overlay
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(4px);
  font-family: "Source Sans Pro", "Helvetica Neue";
`;

// Modal Container
export const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  height: 85vh;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  animation: ${slideUp} 0.3s ease-out;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 90vh;
    max-height: none;
    border-radius: 12px;
    margin: 10px;
    max-width: calc(100vw - 20px);
  }
`;

// Modal Header
export const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 24px;
    right: 24px;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  }
`;

export const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${({ themeColor }) => themeColor || '#32BAF1'};
  margin: 0;
  font-family: "Source Sans Pro", "Helvetica Neue";
  letter-spacing: -0.02em;
  text-align: center;
  flex: 1;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  color: #6b7280;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }
  
  &:active {
    transform: scale(0.95);
    transition: all 0.1s ease;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: rotate(90deg) scale(1.1);
  }
`;

// Messages Container
export const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(16, 185, 129, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 40% 60%, rgba(139, 92, 246, 0.02) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

// Message Item
export const MessageItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  animation: ${fadeIn} 0.3s ease-out;
  align-items: ${props => props.isCurrentUser ? 'flex-end' : 'flex-start'};
`;

export const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: ${props => props.isCurrentUser ? 'row-reverse' : 'row'};
`;

export const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.isCurrentUser 
    ? (props.themeColor || '#EE6D29')
    : '#6b7280'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
  ${props => props.isCurrentUser && `
    border: 2px solid ${props.themeColor || '#EE6D29'};
    background: white;
    color: ${props.themeColor || '#EE6D29'};
  `}
`;

export const UserName = styled.span`
  font-weight: 600;
  color: ${props => props.isCurrentUser ? (props.themeColor || '#EE6D29') : '#1f2937'};
  font-size: 14px;
  font-family: 'Source Sans Pro', sans-serif;
`;

export const MessageTime = styled.span`
  color: #6b7280;
  font-size: 12px;
  margin-left: auto;
`;

export const MessageContent = styled.div`
  background: ${props => props.isCurrentUser 
    ? (props.themeColor ? `${props.themeColor}15` : '#EE6D2915')
    : '#f8fafc'
  };
  padding: 12px 16px;
  border-radius: 12px;
  margin-left: ${props => props.isCurrentUser ? '0' : '40px'};
  margin-right: ${props => props.isCurrentUser ? '40px' : '0'};
  color: #374151;
  line-height: 1.5;
  font-size: 14px;
  font-family: 'Source Sans Pro', sans-serif;
  border: 1px solid ${props => props.isCurrentUser 
    ? (props.themeColor || '#EE6D29')
    : '#e2e8f0'
  };
  max-width: 80%;
  word-wrap: break-word;
  position: relative;
  
  &:hover .message-actions {
    opacity: 1;
  }
`;

export const EditMessageContent = styled(MessageContent)`
 padding: 0 10px 10px 0;
`;

// Message Actions Container
export const MessageActions = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 4px;
  opacity: 1;
  transition: all 0.3s ease;
  justify-content: ${props => props.isCurrentUser ? 'flex-end' : 'flex-start'};
  margin-left: ${props => props.isCurrentUser ? '0' : '40px'};
  margin-right: ${props => props.isCurrentUser ? '40px' : '0'};
`;

// Action Button
export const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  
  &:hover {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  }
  
  &:active {
    transform: scale(0.95);
    transition: all 0.1s ease;
  }
  
  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: scale(1.1);
  }
`;

// Edit Mode Container
export const EditModeContainer = styled.div`
  background: ${props => props.isCurrentUser 
    ? (props.themeColor ? `${props.themeColor}08` : '#EE6D2908')
    : '#ffffff'
  };
  padding: 14px;
  border-radius: 16px;
  margin-left: ${props => props.isCurrentUser ? '0' : '40px'};
  margin-right: ${props => props.isCurrentUser ? '40px' : '0'};
  border: 2px solid ${props => props.isCurrentUser 
    ? (props.themeColor || '#EE6D29')
    : '#e5e7eb'
  };
  max-width: 75%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
`;

// Edit Input
export const EditInput = styled.textarea`
  width: 100%;
  min-height: 60px;
  max-height: 120px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  font-family: 'Source Sans Pro', sans-serif;
  resize: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  line-height: 1.5;
  margin-bottom: 10px;
  background: transparent;
  border: none;
  color: #1f2937;
  
  &:focus {
    outline: none;
   border: none;
  }
`;

// Edit Actions
export const EditActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

// Edit Button
export const EditButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &.save {
    background: ${props => props.themeColor || '#32baf1'};
    color: white;
    
    &:hover:not(:disabled) {
      background: ${props => props.themeColor ? `${props.themeColor}dd` : '#2ba3d4'};
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
    
    &:disabled {
      background: #d1d5db;
      cursor: not-allowed;
      box-shadow: none;
    }
  }
  
  &.cancel {
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    
    &:hover {
      background: #e5e7eb;
      color: #374151;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
`;

// Edited Indicator
export const EditedIndicator = styled.span`
  color: #6b7280;
  font-size: 11px;
  font-style: italic;
  margin-left: 8px;
  opacity: 0.8;
`;

// Confirmation Modal
export const ConfirmationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1002;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

export const ConfirmationModal = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.2s ease-out;
  overflow: hidden;
`;

export const ConfirmationHeader = styled.div`
  padding: 28px 24px 0;
  text-align: center;
`;

export const ConfirmationIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.15);
  
  svg {
    width: 28px;
    height: 28px;
    color: #dc2626;
  }
`;

export const ConfirmationTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 10px 0;
  font-family: "Source Sans Pro", "Helvetica Neue";
  letter-spacing: -0.02em;
`;

export const ConfirmationMessage = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 28px 0;
  line-height: 1.6;
  font-family: "Source Sans Pro", sans-serif;
`;

export const ConfirmationActions = styled.div`
  padding: 0 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: center;
`;

export const ConfirmationButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;
  font-family: "Source Sans Pro", sans-serif;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &.cancel {
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    
    &:hover {
      background: #e5e7eb;
      color: #374151;
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  &.delete {
    background: #dc2626;
    color: white;
    
    &:hover:not(:disabled) {
      background: #b91c1c;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
    
    &:disabled {
      background: #fca5a5;
      cursor: not-allowed;
      box-shadow: none;
    }
  }
`;

// Input Area
export const InputArea = styled.div`
  padding: 16px 24px;
  border-top: none;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06), 0 -1px 3px rgba(0, 0, 0, 0.04);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 24px;
    right: 24px;
    height: 1px;
    background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  }
`;

export const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const MessageInput = styled.textarea`
  width: 100%;
  min-height: 56px;
  max-height: 120px;
  padding: 16px 60px 16px 16px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  font-size: 15px;
  font-family: "Source Sans Pro", "Helvetica Neue";
  resize: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  line-height: 1.4;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:focus {
    outline: none;
    border-color: ${props => props.themeColor || '#32baf1'};
    box-shadow: 0 0 0 3px ${props => props.themeColor ? `${props.themeColor}1A` : 'rgba(50, 186, 241, 0.1)'}, 
                0 1px 3px rgba(0, 0, 0, 0.08);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
  
  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

export const SendButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.themeColor || '#EE6D29'};
  border: none;
  border-radius: 12px;
  padding: 10px;
  color: white;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  box-shadow: 0 2px 6px ${props => props.themeColor ? `${props.themeColor}40` : 'rgba(50, 186, 241, 0.25)'};
  
  &:hover:not(:disabled) {
    transform: translateY(-50%) translateY(-1px);
    box-shadow: 0 4px 12px ${props => props.themeColor ? `${props.themeColor}4D` : 'rgba(50, 186, 241, 0.3)'};
  }
  
  &:active:not(:disabled) {
    transform: translateY(-50%);
  }
  
  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    transform: translateY(-50%);
    box-shadow: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Loading States
export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #6b7280;
  font-size: 14px;
`;

export const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid ${props => props.themeColor || '#32baf1'};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 12px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
`;

export const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  
  svg {
    width: 32px;
    height: 32px;
    color: #9ca3af;
  }
`;

export const EmptyStateText = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 8px 0;
  color: #374151;
`;

export const EmptyStateSubtext = styled.p`
  font-size: 14px;
  margin: 0;
  color: #6b7280;
`;

// Error State
export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #dc2626;
`;

export const ErrorText = styled.p`
  font-size: 14px;
  margin: 0;
`;

// Character Counter
export const CharacterCounter = styled.div`
  font-size: 12px;
  color: ${props => props.isOverLimit ? '#dc2626' : '#6b7280'};
  text-align: right;
  margin-top: 8px;
  padding-right: 4px;
`;

// Notification Badge (for future use)
export const NotificationBadge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  background: #dc2626;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
`;

// Tab-related styles
export const TabsContainer = styled.div`
  display: flex;
  background-color: #fff;
  padding: 16px 20px 12px;
  gap: 12px;
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 2px;
  }
`;

export const Tab = styled.button`
  padding: 12px 24px;
  border: 2px solid ${props => props.active ? (props.themeColor || '#0097B2') : '#e2e8f0'};
  background: ${props => props.active 
    ? `linear-gradient(135deg, ${props.themeColor || '#0097B2'}, ${props.themeColor ? `${props.themeColor}dd` : '#007a94'})`
    : 'linear-gradient(135deg, #ffffff, #f8fafc)'
  };
  color: ${props => props.active ? '#fff' : '#64748b'};
  font-size: 14px;
  font-weight: ${props => props.active ? '700' : '600'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  min-width: 140px;
  border-radius: 28px;
  letter-spacing: -0.01em;
  box-shadow: ${props => props.active 
    ? `0 4px 12px ${props.themeColor ? `${props.themeColor}40` : 'rgba(0, 151, 178, 0.25)'}, 0 2px 4px ${props.themeColor ? `${props.themeColor}20` : 'rgba(0, 151, 178, 0.15)'}`
    : '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)'
  };
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    background: ${props => props.active 
      ? `linear-gradient(135deg, ${props.themeColor ? `${props.themeColor}ee` : '#0087a3'}, ${props.themeColor || '#0097B2'})`
      : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)'
    };
    border-color: ${props => props.active ? (props.themeColor || '#0097B2') : '#cbd5e1'};
    color: ${props => props.active ? '#fff' : '#374151'};
    transform: translateY(-2px) scale(1.02);
    box-shadow: ${props => props.active 
      ? `0 6px 20px ${props.themeColor ? `${props.themeColor}50` : 'rgba(0, 151, 178, 0.35)'}, 0 3px 8px ${props.themeColor ? `${props.themeColor}30` : 'rgba(0, 151, 178, 0.2)'}`
      : '0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06)'
    };
    
    &::before {
      left: 100%;
    }
  }
  
  &:focus {
    outline: 2px solid ${props => props.themeColor || '#0097B2'};
    outline-offset: 2px;
  }
  
  &:active {
    transform: translateY(-1px) scale(1.01);
    transition: all 0.1s ease;
  }
`;

export const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const SlideTitle = styled.h3`
  margin: 0;
  padding: 14px 28px 4px;
  font-size: 18px;
  font-weight: 700;
  color: #EE6D29;
  background-color: #fff;
  letter-spacing: -0.02em;
`;
export const SlideDescription = styled.div`
  margin: 0;
  padding: 0 28px 14px;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  p {
    margin: 0;
  }
`;

export const TabContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;
